import { useCallback, useEffect, useState } from 'react';
import { PaymentInfo, PaymentMethod } from '../../common/types/CartType';

// The one order a guest currently has in flight, remembered across a
// closed drawer and a reloaded page.
//
// Without this the order is unreachable the moment the guest dismisses
// the confirmation: the basket is empty by then, so the floating button
// hides itself and there is no route to an order anywhere in the app.
// Anything the restaurant wants to tell the guest would have nowhere to
// land.

const STORAGE_PREFIX = 'tlg.activeOrder';

// Long enough to cover a meal, short enough that yesterday's order does
// not greet a guest who comes back the next day.
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

export interface ActiveOrder {
    orderId: string;
    /** ISO. Only used to expire the record. */
    placedAt: string;
    method: PaymentMethod;
    /** Non-null only while a card still has to be charged. */
    payment: PaymentInfo | null;
}

function keyFor(menuId: number): string {
    return `${STORAGE_PREFIX}.${menuId}`;
}

function isFresh(order: ActiveOrder): boolean {
    const placed = new Date(order.placedAt).getTime();
    return Number.isFinite(placed) && Date.now() - placed < MAX_AGE_MS;
}

function read(menuId: number | null): ActiveOrder | null {
    if (menuId === null) return null;
    try {
        const raw = window.localStorage.getItem(keyFor(menuId));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as ActiveOrder;
        if (typeof parsed?.orderId !== 'string' || !parsed.orderId) return null;
        if (!isFresh(parsed)) {
            window.localStorage.removeItem(keyFor(menuId));
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

function write(menuId: number | null, value: ActiveOrder | null): void {
    if (menuId === null) return;
    try {
        if (value) window.localStorage.setItem(keyFor(menuId), JSON.stringify(value));
        else window.localStorage.removeItem(keyFor(menuId));
    } catch {
        /* private mode — the guest loses the resume, not the order */
    }
}

export interface ActiveOrderApi {
    active: ActiveOrder | null;
    /** Called once the order exists on the server. */
    start: (order: Omit<ActiveOrder, 'placedAt'>) => void;
    /** Payment finished — the order lives on, nothing is left to charge. */
    settle: () => void;
    /** Guest is done with it. */
    dismiss: () => void;
}

export function useActiveOrder(menuId: number | null): ActiveOrderApi {
    const [active, setActive] = useState<ActiveOrder | null>(null);

    useEffect(() => {
        setActive(read(menuId));
    }, [menuId]);

    const start = useCallback(
        (order: Omit<ActiveOrder, 'placedAt'>) => {
            const stored: ActiveOrder = { ...order, placedAt: new Date().toISOString() };
            write(menuId, stored);
            setActive(stored);
        },
        [menuId]
    );

    const settle = useCallback(() => {
        setActive((prev) => {
            if (!prev) return prev;
            const stored: ActiveOrder = { ...prev, payment: null };
            write(menuId, stored);
            return stored;
        });
    }, [menuId]);

    const dismiss = useCallback(() => {
        write(menuId, null);
        setActive(null);
    }, [menuId]);

    return { active, start, settle, dismiss };
}
